import { useEffect, useState } from "react";
import TerciaryButton from "@/components/buttons/terciary-button/TerciaryButton";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { literals } from "@/constants/literals";
import { getImageUrl } from "@/lib/services/images";
import { Icon } from "@/components/icons/Icon";
import type { ImageDeletionRequestApi } from "@/types/types.d";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import { formatBooleanText, formatOptionalText } from "@/lib/texts.utils";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";

const RequestDetailMobile = ({
  request,
  cleanRequest,
  onProcess,
}: {
  request: ImageDeletionRequestApi;
  cleanRequest: () => void;
  onProcess: (status: "APPROVED" | "REJECTED") => Promise<void>;
}) => {
  const [photoURL, setPhotoURL] = useState("");

  const fileName = request.s3Key.split("/").pop() ?? request.s3Key;

  useEffect(() => {
    const loadImage = async () => {
      try {
        const url = await getImageUrl(request.imageId);
        setPhotoURL(url);
      } catch (e) {
        console.error("Error loading image", e);
        setPhotoURL("");
      }
    };

    loadImage();
  }, [request.imageId]);

  const metaData = [
    {
      label: literals.date,
      value: formatDateDDMmmYYYY(request.createdAt),
    },
    {
      label: literals.workstation,
      value: formatOptionalText(request.area),
    },
    {
      label: literals.visibleClient,
      value: formatBooleanText(request.imageGroupVisible),
    },
  ];

  const fullData = [
    {
      label: literals.step,
      value: formatOptionalText(request.step),
    },
    {
      label: literals.customerOrderNoServ,
      value: formatOptionalText(request.customerOrderNoserv),
    },
    {
      label: literals.client,
      value: formatOptionalText(request.customer),
    },
    {
      label: literals.partIn,
      value: formatOptionalText(request.orderIn),
    },
    {
      label: literals.serialIn,
      value: formatOptionalText(request.serialIn),
    },
    {
      label: literals.partOut,
      value: formatOptionalText(request.orderOut),
    },
    {
      label: literals.serialOut,
      value: formatOptionalText(request.serialOut),
    },
    {
      label: literals.document9R1,
      value: formatOptionalText(request.document9r1),
    },
    {
      label: literals.returnToOrigin,
      value: formatBooleanText(request.originReturn),
    },
    {
      label: literals.ownPackaging,
      value: formatBooleanText(request.ownPackaging),
    },
    {
      label: literals.correctPackaging,
      value: formatBooleanText(request.correctPackaging),
    },
    {
      label: literals.damagedPackaging,
      value: formatBooleanText(request.damagedPackaging),
    },
    {
      label: literals.packagingRef,
      value: formatOptionalText(request.packagingReference),
    },
    {
      label: literals.workOrder,
      value: formatOptionalText(request.sapWorkOrder),
    },
    {
      label: literals.ownPackagingOut,
      value: formatBooleanText(request.ownPackagingOut),
    },
    {
      label: literals.iberiaPackaging,
      value: formatBooleanText(request.iberiaPackagingOut),
    },
  ];

  return (
    <div data-testid="request-detail-mobile" className="relative">
      <div className="bg-[#EEEEEE] -mx-4 -mt-4 flex fixed w-full z-10">
        <div>
          <TerciaryButton
            onClick={cleanRequest}
            icon="arrowLeft"
            size="small"
            dataTestid="go-back-request-mobile"
            label={literals.goBackList}
            colour="red"
          />
        </div>
      </div>

      <div className="grid gap-2 py-8">
        <div
          className="border border-[#FCD100] bg-[#FFFAE0] text-[#464646] font-ib-bold p-2 mb-4 rounded-sm flex gap-2 w-full"
          data-testid="deletion-request-warning"
        >
          <Icon name="warningTriangle" className="text-[#C7A400]" />
          <p>{literals.wishDeletePhoto}</p>
        </div>

        <h1 className="text-xl">{fileName}</h1>

        {photoURL ? (
          <img src={photoURL} alt={fileName} className="w-full rounded-sm" />
        ) : (
          <div className="border rounded-sm p-8 text-center text-sm text-gray-500">
            {literals.noChargedImage}
          </div>
        )}

        <div className="grid gap-4">
          <div className="grid gap-x-2 gap-y-4 grid-cols-3">
            {metaData.map((data) => (
              <BlockInfo
                key={data.label}
                label={data.label}
                value={data.value}
              />
            ))}
          </div>

          <BlockInfo
            label={literals.description}
            value={request.imageGroupDescription || ""}
          />

          <div className="grid gap-x-2 gap-y-4 grid-cols-2">
            {fullData.map((data) => (
              <BlockInfo
                key={data.label}
                label={data.label}
                value={data.value}
              />
            ))}
          </div>
        </div>

        <div className="grid gap-3 mt-4">
          <PrimaryButton
            size="medium"
            label={literals.acceptPetition}
            colour="red"
            type="button"
            onClick={() => onProcess("APPROVED")}
          />

          <SecondaryButton
            size="medium"
            label={literals.rejectPetition}
            colour="red"
            buttonType="button"
            onClick={() => onProcess("REJECTED")}
          />
        </div>
      </div>
    </div>
  );
};

export default RequestDetailMobile;
const BlockInfo = ({ label, value }: { label: string; value: string }) => {
  return (
    <div
      data-testid={`block-info-${label}`}
      className="flex flex-col text-sm gap-1"
    >
      <p className="text-[#666666]">{label}</p>
      <p className="text-[#333333] font-ib-bold break-words">{value}</p>
    </div>
  );
};
